import { Component, Input, OnInit } from '@angular/core';
import { WidgetComponent } from '@widgets/widgets';
import { Metric } from '@widgets/models/Metric';
import { DashboardService } from '@widgets/services/dashboard.service';
import {
  DASHBOARD_TIME_PERIOD_FILTERS,
  DASHBOARD_MONTH_FILTER_INDEX,
  NPS_VALUE_STATE
} from '@widgets/assets/constants';

@Component({
  selector: 'lms-nps',
  templateUrl: './nps.component.html',
  styleUrls: ['./nps.component.scss']
})
export class NpsComponent implements WidgetComponent, OnInit {
  @Input() public data: any;
  public metricNps: Metric;
  public widgetId: string = 'NPS';
  public filterList = DASHBOARD_TIME_PERIOD_FILTERS;
  public defaultFilter: any;
  public npsValue: number = 0;
  public npsColor: string = '';
  public minValue = -100;
  public maxValue = 100;
  public gap: number = 3; // % handle gap
  private middleValue = 50;
  private minValueProgress = 6;

  constructor(public dashboardService: DashboardService) {}

  public ngOnInit(): void {
    this.setNpsData(this.data['metric']);
    const client = this.data.managedAccounts.length === 1 ? this.data.managedAccounts[0] : null;

    // Remove subscription filter for credits clients
    const isCreditsClient = client && client.subscriptions.credits;
    this.filterList =
      isCreditsClient || this.data.managedAccounts.length > 1
        ? DASHBOARD_TIME_PERIOD_FILTERS.filter((option) => option.filter !== 'subscription')
        : DASHBOARD_TIME_PERIOD_FILTERS;
    this.defaultFilter = this.filterList[DASHBOARD_MONTH_FILTER_INDEX];
  }

  private setNpsData(metric: Metric) {
    this.metricNps = metric;
    this.npsValue = this.progressChart(metric.ratioNPS);
    this.npsColor = this.setColorValue(this.npsValue);
  }

  /**
   * Set and request the filter selected
   * @param selectedFilter
   */
  public setFilter(selectedFilter: any): void {
    if (selectedFilter.filter !== this.defaultFilter.filter) {
      this.defaultFilter = selectedFilter;
      this.dashboardService
        .getWidgetData(this.data['clientIds'], 'metrics', { timePeriod: selectedFilter['filter'] })
        .subscribe((metric: Metric) => {
          this.setNpsData(metric);
        });
    }
  }

  /**
   * Calculate the value of the NPS progress chart
   * @param npsValue
   * @private
   */
  public progressChart(npsValue: number): number {
    if (Number.isFinite(npsValue)) {
      if (npsValue === 0) {
        return this.middleValue + this.gap;
      }
      if (npsValue === this.minValue) {
        return this.maxValue;
      }
      if (npsValue === this.maxValue) {
        return this.minValueProgress;
      }
      if (npsValue < 0) {
        return this.middleValue + (npsValue * (this.middleValue - this.maxValue)) / this.maxValue;
      }
      if (npsValue > 0) {
        this.gap = npsValue === 30 || npsValue === 70 ? this.gap : 0;
        return this.middleValue - (npsValue * (this.maxValue - this.middleValue)) / this.maxValue + this.gap;
      }
    }
  }

  /**
   * Set NPS progress color
   * progress 100% === -100 NPS (minValue) Mejorable
   * progress 50% === 0 NPS Mejorable
   * progress 35% === 30 NPS Bueno
   * progress 15% === 70 NPS Muy bueno
   * progress 6% === 100 NPS (minValue / maxValue) Excelente
   * @param npsValue
   * @private
   */
  private setColorValue(npsValue: number): string {
    if (npsValue >= 50) {
      return 'coral';
    }
    return npsValue >= NPS_VALUE_STATE.well
      ? 'green-light'
      : npsValue >= NPS_VALUE_STATE.brilliant && npsValue < NPS_VALUE_STATE.well
      ? 'green'
      : npsValue < NPS_VALUE_STATE.brilliant
      ? 'greeny-blue'
      : 'default';
  }
}
